import { ReactNode } from 'react';
import {
  ArrowLeft,
  Search,
  Shield,
  Users,
  UserMinus,
  MoreVertical,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/formatDate';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export interface IMemberRow {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  lastActiveAt?: string | null;
  messageCount?: number;
  /** Space members: the admin-side member mute (`status = muted`). */
  isMuted?: boolean;
  /** Room members: set while the member is muted in that chat room. */
  mutedAt?: string | null;
  user: {
    id?: string;
    username: string;
    displayName: string;
    avatar: string | null;
  } | null;
}

export interface IStatCard {
  label: string;
  value: string;
  icon: ReactNode;
  bg: string;
  onClick?: () => void;
}

interface IMemberListLayoutProps {
  onBack: () => void;
  backLabel: string;
  headerIcon: ReactNode;
  title: string;
  subtitle: string;
  headerAction?: ReactNode;
  stats: IStatCard[];
  searchPlaceholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  roleFilter: string;
  onRoleFilterChange: (role: string) => void;
  filterLabels: { all: string; admin: string; member: string };
  members: IMemberRow[];
  loading: boolean;
  showMessageCount?: boolean;
  getRoleBadge: (role: string) => ReactNode;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onViewUser?: (member: IMemberRow) => void;
  onToggleAdmin?: (member: IMemberRow) => void;
  onRemoveMember?: (member: IMemberRow) => void;
}

const MemberListLayout = ({
  onBack,
  backLabel,
  headerIcon,
  title,
  subtitle,
  headerAction,
  stats,
  searchPlaceholder,
  search,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  filterLabels,
  members,
  loading,
  showMessageCount = false,
  getRoleBadge,
  page,
  totalPages,
  onPageChange,
  onViewUser,
  onToggleAdmin,
  onRemoveMember,
}: IMemberListLayoutProps) => {
  const { t } = useTranslation();
  const colSpan = showMessageCount ? 6 : 5;

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-[#0A0A0A] hover:text-gray-900 w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          {headerIcon}
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              {title}
            </h1>
            <p className="text-base text-muted-foreground mt-1">{subtitle}</p>
          </div>
        </div>
        {headerAction}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map(({ label, value, icon, onClick }) => (
          <Card
            key={label}
            className={`shadow-sm ${onClick ? 'cursor-pointer hover:border-blue-300 transition-colors' : ''}`}
            onClick={onClick}
          >
            <CardContent className="py-5 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {label}
                  </div>
                  <div className="text-[26px] font-bold leading-[1.2] text-foreground">
                    {value}
                  </div>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Search className="w-5 h-5" />
              </span>
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={roleFilter === 'all' ? 'default' : 'outline'}
                onClick={() => onRoleFilterChange('all')}
              >
                {filterLabels.all}
              </Button>
              <Button
                variant={roleFilter === 'admin' ? 'default' : 'outline'}
                onClick={() => onRoleFilterChange('admin')}
                className="gap-2"
              >
                <Shield className="w-4 h-4" />
                {filterLabels.admin}
              </Button>
              <Button
                variant={roleFilter === 'member' ? 'default' : 'outline'}
                onClick={() => onRoleFilterChange('member')}
              >
                {filterLabels.member}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className="gap-0 overflow-hidden">
        <Table style={{ minWidth: '800px' }}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('chatroom.member_name')}</TableHead>
              <TableHead>{t('common.role')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('chatroom.join_date')}</TableHead>
              {showMessageCount && (
                <TableHead>{t('chatroom.message_count')}</TableHead>
              )}
              <TableHead className="text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="px-6 py-12 text-center text-sm text-muted-foreground"
                >
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="px-6 py-12 text-center text-sm text-muted-foreground"
                >
                  {t('rooms.no_members')}
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow
                  key={member.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => onViewUser?.(member)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-medium shrink-0 overflow-hidden"
                        style={{ backgroundColor: '#3b82f6' }}
                      >
                        {member.user?.avatar ? (
                          <img
                            src={getImageUrl(member.user.avatar)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          (member.user?.displayName || '?').charAt(0)
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {member.user?.displayName || t('rooms.unknown_user')}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {member.user?.username || '—'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(member.role)}</TableCell>
                  <TableCell>
                    {member.isMuted || member.mutedAt ? (
                      <Badge variant="warning" className="rounded-full">
                        {t('common.muted')}
                      </Badge>
                    ) : (
                      <Badge variant="success" className="rounded-full">
                        {t('common.active')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(member.joinedAt)}
                  </TableCell>
                  {showMessageCount && (
                    <TableCell className="text-sm text-muted-foreground">
                      {(member.messageCount ?? 0).toLocaleString()}
                    </TableCell>
                  )}
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[160px]"
                      >
                        {onViewUser && (
                          <DropdownMenuItem onClick={() => onViewUser(member)}>
                            {t('members.view_user_detail')}
                          </DropdownMenuItem>
                        )}
                        {onToggleAdmin &&
                          member.role !== 'owner' &&
                          member.role !== 'space_admin' && (
                            <DropdownMenuItem
                              onClick={() => onToggleAdmin(member)}
                              className={
                                member.role === 'admin'
                                  ? 'text-orange-600 focus:text-orange-600'
                                  : 'text-blue-600 focus:text-blue-600'
                              }
                            >
                              <Shield className="w-4 h-4 mr-1" />
                              {member.role === 'admin'
                                ? t('members.remove_admin')
                                : t('members.set_admin')}
                            </DropdownMenuItem>
                          )}
                        {onRemoveMember &&
                          member.role !== 'owner' &&
                          member.role !== 'space_admin' && (
                            <DropdownMenuItem
                              onClick={() => onRemoveMember(member)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <UserMinus className="w-4 h-4 mr-1" />
                              {t('members.remove_member')}
                            </DropdownMenuItem>
                          )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('common.page_info', {
              page: page.toString(),
              totalPages: totalPages.toString(),
            })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              {t('common.prev_page')}
            </Button>
            <Button
              variant="outline"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >
              {t('common.next_page')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberListLayout;
